import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { ArrowLeft, RefreshCw, Zap, LayoutGrid, Users } from 'lucide-react'
import {
  Platform,
  useGetListeningTopicFeedLazyQuery,
  useRefreshListeningTopicMutation,
  useMarkTopicContentInteractedMutation,
  useHideTopicContentMutation,
} from 'graphql/generated/graphql'
import ContentFeed from 'pages/DiscoverContent/components/ContentFeed'
import PlatformColumn from './PlatformColumn'
import EngagementQueue from './EngagementQueue'
import InfoTooltip from 'components/InfoTooltip/InfoTooltip'
import AnimatedLoadingText from 'components/AnimatedLoadingText/AnimatedLoadingText'

const platformIcons: Record<Platform, string> = {
  [Platform.Instagram]: '📷',
  [Platform.TikTok]: '🎵',
  [Platform.Youtube]: '🎥',
  [Platform.Reddit]: '🔴',
  [Platform.Facebook]: '📘',
  [Platform.Twitter]: '🐦',
  [Platform.Bluesky]: '🦋',
}

interface TopicFeedProps {
  topicId: string
  topicName: string
  topicPlatforms: Platform[]
  lastScrapedAt: string | null
  onBack: () => void
}

const REFRESH_COOLDOWN_MS = 12 * 60 * 60 * 1000 // 12 hours

// Sort: not-interacted first (by similarity desc), then interacted (by similarity desc)
function sortByInteractedThenSimilarity(items: any[]): any[] {
  return [...items].sort((a, b) => {
    if (a.interactedWith !== b.interactedWith) return a.interactedWith ? 1 : -1
    return (b.similarity ?? 0) - (a.similarity ?? 0)
  })
}

type ViewMode = 'queue' | 'feed'

const FOLLOWER_PRESETS = [
  { label: 'All sizes', min: 0, max: 0 },
  { label: '0-1K', min: 1, max: 1000 },
  { label: '1K-10K', min: 1000, max: 10000 },
  { label: '10K-50K', min: 10000, max: 50000 },
  { label: '50K+', min: 50000, max: 0 },
]

export default function TopicFeed({ topicId, topicName, topicPlatforms, lastScrapedAt, onBack }: TopicFeedProps): JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>('queue')
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | ''>('')
  const [followerMin, setFollowerMin] = useState(0)
  const [followerMax, setFollowerMax] = useState(0)
  const [offset, setOffset] = useState(0)
  const [allContent, setAllContent] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const ITEMS_PER_PAGE = 30

  const [fetchFeed, { loading }] = useGetListeningTopicFeedLazyQuery()
  const [refreshTopic, { loading: refreshing }] = useRefreshListeningTopicMutation()
  const [markInteracted] = useMarkTopicContentInteractedMutation()
  const [hideContent] = useHideTopicContentMutation()

  // Client-side follower count filtering
  const filteredContent = allContent.filter((item: any) => {
    if (followerMin === 0 && followerMax === 0) return true
    const fc = item.authorFollowerCount ?? 0
    if (fc === 0) return false // Exclude unknowns when filtering
    if (followerMin > 0 && fc < followerMin) return false
    if (followerMax > 0 && fc > followerMax) return false
    return true
  })

  const handleInteract = (contentId: string): void => {
    // Optimistic local update — mark interacted, then re-sort to push to bottom
    setAllContent((prev) => {
      const updated = prev.map((c) =>
        c.id === contentId ? { ...c, interactedWith: true } : c
      )
      return sortByInteractedThenSimilarity(updated)
    })
    markInteracted({ variables: { contentId } }).catch(console.error)
  }

  const handleHide = (contentId: string): void => {
    // Remove from local state immediately
    setAllContent((prev) => prev.filter((c) => c.id !== contentId))
    hideContent({ variables: { contentId } }).catch(console.error)
  }

  const loadContent = async (newOffset: number, append: boolean = false): Promise<void> => {
    const result = await fetchFeed({
      variables: {
        input: {
          topicId,
          platform: selectedPlatform === '' ? undefined : selectedPlatform,
          limit: ITEMS_PER_PAGE,
          offset: newOffset,
        },
      },
    })

    const feed = result.data?.getListeningTopicFeed
    if (feed) {
      setAllContent((prev) => (append ? [...prev, ...feed.content] : feed.content))
      setHasMore(feed.hasMore)
      setOffset(newOffset + ITEMS_PER_PAGE)
    }
  }

  // Skip the unified fetch when we're showing per-platform columns (each column fetches its own)
  const showColumns = selectedPlatform === '' && topicPlatforms.length > 1

  useEffect(() => {
    setAllContent([])
    setOffset(0)
    setHasMore(true)
    if (!showColumns) {
      loadContent(0, false).catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, selectedPlatform])

  // Auto-poll for results when topic was just created (no lastScrapedAt and no content)
  useEffect(() => {
    if (lastScrapedAt != null || allContent.length > 0 || showColumns) return
    const poll = setInterval(() => {
      loadContent(0, false).catch(console.error)
    }, 8000)
    return () => clearInterval(poll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, lastScrapedAt, allContent.length, showColumns])

  const [refreshStarted, setRefreshStarted] = useState(false)

  const canRefresh = (): boolean => {
    if (!lastScrapedAt) return true
    const elapsed = Date.now() - new Date(lastScrapedAt).getTime()
    return elapsed >= REFRESH_COOLDOWN_MS
  }

  const handleRefresh = async (): Promise<void> => {
    await refreshTopic({ variables: { id: topicId } })
    setRefreshStarted(true)
    // Content is being scraped in the background — poll for new results
    const poll = (attempts: number): void => {
      if (attempts <= 0) {
        setRefreshStarted(false)
        return
      }
      setTimeout(() => {
        loadContent(0, false)
          .then(() => {
            if (attempts > 1) poll(attempts - 1)
            else setRefreshStarted(false)
          })
          .catch(() => { setRefreshStarted(false) })
      }, 10000) // Check every 10 seconds
    }
    poll(6) // Poll for up to 60 seconds
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={onBack} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: 'white', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '18px', md: '24px' } }}>
          {topicName}
        </Typography>
        <Button
          onClick={() => { handleRefresh().catch(console.error) }}
          disabled={refreshing || refreshStarted || !canRefresh()}
          startIcon={(refreshing || refreshStarted) ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            fontSize: { xs: '12px', md: '13px' },
            px: { xs: 1, md: 1.5 },
            '&:hover': { borderColor: 'rgba(168, 85, 247, 0.3)' },
          }}
        >
          {refreshing ? 'Refreshing...' : refreshStarted ? 'Pulling...' : !canRefresh() ? 'Recent' : 'Refresh'}
        </Button>
      </Box>

      {/* View mode toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          onClick={() => setViewMode('queue')}
          startIcon={<Zap size={14} />}
          sx={{
            flex: 1,
            backgroundColor: viewMode === 'queue' ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.03)',
            border: viewMode === 'queue' ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.1)',
            color: viewMode === 'queue' ? 'white' : 'rgba(255,255,255,0.5)',
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: { xs: '12px', md: '13px' },
            fontWeight: 600,
            px: { xs: 1, md: 2 },
          }}
        >
          Engage<InfoTooltip text="One conversation at a time with an AI-generated comment ready to go. Review, edit, and reply — the fastest way to engage." size={12} />
        </Button>
        <Button
          onClick={() => setViewMode('feed')}
          startIcon={<LayoutGrid size={14} />}
          sx={{
            flex: 1,
            backgroundColor: viewMode === 'feed' ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.03)',
            border: viewMode === 'feed' ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.1)',
            color: viewMode === 'feed' ? 'white' : 'rgba(255,255,255,0.5)',
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: { xs: '12px', md: '13px' },
            fontWeight: 600,
            px: { xs: 1, md: 2 },
          }}
        >
          Browse<InfoTooltip text="See all conversations in a feed layout. When viewing all platforms, each platform gets its own scrollable column." size={12} />
        </Button>
      </Box>

      {/* Follower size filter */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, alignItems: 'center' }}>
        <Users size={14} color="rgba(255,255,255,0.4)" />
        {FOLLOWER_PRESETS.map((preset) => (
          <Chip
            key={preset.label}
            label={preset.label}
            size="small"
            onClick={() => { setFollowerMin(preset.min); setFollowerMax(preset.max) }}
            sx={{
              backgroundColor: followerMin === preset.min && followerMax === preset.max
                ? 'rgba(168,85,247,0.25)'
                : 'rgba(255,255,255,0.05)',
              border: followerMin === preset.min && followerMax === preset.max
                ? '1px solid rgba(168,85,247,0.4)'
                : '1px solid rgba(255,255,255,0.08)',
              color: followerMin === preset.min && followerMax === preset.max
                ? 'white'
                : 'rgba(255,255,255,0.5)',
              fontSize: '11px',
              '&:hover': { backgroundColor: 'rgba(168,85,247,0.15)' },
            }}
          />
        ))}
      </Box>

      {/* Queue mode */}
      {viewMode === 'queue' && (
        <EngagementQueue topicId={topicId} topicName={topicName} />
      )}

      {/* Feed mode — Platform Filter + content */}
      {viewMode === 'feed' && (
        <>
      {/* Platform Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label="All"
          onClick={() => setSelectedPlatform('')}
          sx={{
            backgroundColor: selectedPlatform === '' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(255,255,255,0.05)',
            border: selectedPlatform === '' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid rgba(255,255,255,0.1)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.2)' },
          }}
        />
        {topicPlatforms.map((p) => (
          <Chip
            key={p}
            label={`${platformIcons[p]} ${p}`}
            onClick={() => setSelectedPlatform(p)}
            sx={{
              backgroundColor: selectedPlatform === p ? 'rgba(168, 85, 247, 0.3)' : 'rgba(255,255,255,0.05)',
              border: selectedPlatform === p ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid rgba(255,255,255,0.1)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.2)' },
            }}
          />
        ))}
      </Box>

      {/* Content */}
      {selectedPlatform === '' && topicPlatforms.length > 1 ? (
        // Multi-platform vertical column view
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
          {topicPlatforms.map((p) => (
            <PlatformColumn key={p} topicId={topicId} platform={p} />
          ))}
        </Box>
      ) : loading && allContent.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#a855f7' }} />
        </Box>
      ) : filteredContent.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          {allContent.length === 0 && !lastScrapedAt ? (
            <>
              <CircularProgress sx={{ color: '#a855f7', mb: 3 }} size={36} />
              <AnimatedLoadingText
                messages={[
                  'Scanning Instagram for conversations',
                  'Searching TikTok for relevant posts',
                  'Checking Reddit for discussions',
                  'Analyzing content relevance',
                  'This can take a minute or two',
                ]}
                interval={3000}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', mt: 2 }}>
                Your first scan is running. Results will appear automatically.
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px' }}>
              {allContent.length === 0 ? 'No content yet. Click Refresh to pull in the latest posts.' : 'No posts match this follower range.'}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <ContentFeed content={filteredContent} onInteract={handleInteract} onHide={handleHide} />

          {/* Load More */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 4 }}>
              <Button
                onClick={() => { loadContent(offset, true).catch(console.error) }}
                disabled={loading}
                sx={{
                  color: 'white',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  px: 4,
                  '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
                }}
              >
                {loading ? <CircularProgress size={16} sx={{ color: '#a855f7' }} /> : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
        </>
      )}
    </Box>
  )
}
